// src/components/PieChart.js
import React, { useEffect, useState } from 'react';
import {
  Treemap,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { CircularProgress, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { auth, db } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Didascalia (leggenda) personalizzata
const CustomTreemapLegend = ({ categories }) => {
  /**
   * categories è l'array di oggetti:
   * [ { name, originalName, size, fill }, {...} ]
   * Qui mostriamo il colore e il nome completo.
   */
  return (
    <Box sx={{ mt: 2 }}>
      {categories.map((child, index) => (
        <Box key={index} display="flex" alignItems="center" mb={0.5}>
          <Box
            sx={{
              width: 16,
              height: 16,
              backgroundColor: child.fill,
              mr: 1,
            }}
          />
          <Typography variant="body2">
            {child.originalName} {/* Nome completo */}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const CATEGORY_COLORS = [
  '#1abc9c',
  '#3498db',
  '#9b59b6',
  '#e74c3c',
  '#f1c40f',
  '#e67e22',
  '#2ecc71',
  '#34495e',
  '#16a085',
  '#2980b9',
];

const PieChart = ({ type, userFamily, year, month }) => {
  const [treemapData, setTreemapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Salva i dati utente in localStorage (logica invariata)
  const saveUserToLocalStorage = (user) => {
    if (user) {
      localStorage.setItem('user', JSON.stringify({
        uid: user.uid,
        email: user.email,
      }));
    }
  };

  const fetchPieData = async (currentUser) => {
    if (!currentUser) return;

    try {
      let expensesQuery;

      // Logica invariata di data in base a "monthly" / "annual"
      if (type === 'monthly') {
        const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        const endDate = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59);

        expensesQuery = userFamily && userFamily.id
          ? query(
              collection(db, 'expenses'),
              where('familyId', '==', userFamily.id),
              where('date', '>=', startDate),
              where('date', '<=', endDate)
            )
          : query(
              collection(db, 'expenses'),
              where('userId', '==', currentUser.uid),
              where('date', '>=', startDate),
              where('date', '<=', endDate)
            );
      } else if (type === 'annual') {
        const startDate = new Date(parseInt(year), 0, 1);
        const endDate = new Date(parseInt(year), 11, 31, 23, 59, 59);

        expensesQuery = userFamily && userFamily.id
          ? query(
              collection(db, 'expenses'),
              where('familyId', '==', userFamily.id),
              where('date', '>=', startDate),
              where('date', '<=', endDate)
            )
          : query(
              collection(db, 'expenses'),
              where('userId', '==', currentUser.uid),
              where('date', '>=', startDate),
              where('date', '<=', endDate)
            );
      }

      const snapshot = await getDocs(expensesQuery);
      const expensesData = snapshot.docs.map((doc) => doc.data());

      const categoryMap = {};
      let total = 0;

      // Sommiamo gli importi per categoria
      expensesData.forEach((expense) => {
        const category = expense.category || 'Non specificata';
        const amount = expense.amount || 0;
        categoryMap[category] = (categoryMap[category] || 0) + amount;
        total += amount;
      });

      // Ordine discendente (dalla cat. più costosa alla meno costosa)
      const sortedEntries = Object.entries(categoryMap).sort((a, b) => b[1] - a[1]);

      // Creiamo i children per la root del Treemap
      const children = sortedEntries.map(([category, amount], index) => {
        // Troncamento del nome da mostrare nel rettangolo (se > 20 caratteri)
        // Così evitiamo che sfori nella resa grafica.
        const truncatedName = category.length > 20
          ? category.substring(0, 20) + '...'
          : category;

        return {
          // name tronca, per disegno
          name: truncatedName,
          // originalName è il nome reale, usato per tooltip/leggenda
          originalName: category,
          size: amount,
          fill: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
        };
      });

      // Treemap si aspetta un array root. Avremo un solo oggetto root con children
      const finalData = [
        {
          name: 'root',
          children,
          totalAmount: total,
        },
      ];

      setTreemapData(finalData);
      setError(null);
    } catch (err) {
      console.error('Errore nel recupero dei dati per il Treemap:', err);
      setError('Errore nel caricamento dei dati.');
      setTreemapData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        saveUserToLocalStorage(user);
        fetchPieData(user);
      } else {
        setError('Utente non autenticato');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [type, year, month, userFamily]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>
        {error}
      </Typography>
    );
  }

  // Se non ci sono dati
  if (treemapData.length === 0 || treemapData[0].children.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
        Nessuna spesa disponibile per il periodo selezionato.
      </Typography>
    );
  }

  const chartHeight = isMobile ? 300 : 400;

  // Per calcolare la percentuale nel tooltip
  const totalValue = treemapData[0].children.reduce((acc, c) => acc + c.size, 0);

  // Tooltip personalizzato: "€X.XX (XX.X%)"
  const tooltipFormatter = (value, name, props) => {
    const percentage = (value / totalValue) * 100;
    /**
     * props.payload conterrà, tra le altre cose,
     *  - name: truncatedName
     *  - originalName: stringa completa
     */
    const original = props.payload.originalName || name;
    return [`€${value.toFixed(2)} (${percentage.toFixed(1)}%)`, original];
  };

  return (
    <>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <Treemap
          data={treemapData}
          dataKey="size"
          aspectRatio={4 / 3}
          stroke="#fff"
        >
          <Tooltip formatter={tooltipFormatter} />
        </Treemap>
      </ResponsiveContainer>

      {/* Didascalia personalizzata per mostrare tutti i colori + nomi completi */}
      <CustomTreemapLegend categories={treemapData[0].children} />
    </>
  );
};

export default PieChart;
